<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-10" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
                leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <div>
                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" class="text-lg font-semibold text-gray-900">
                                        Activity Log
                                    </DialogTitle>
                                    <p class="mt-1 text-sm text-gray-500">Logs are listed from newest to oldest.</p>
                                    <hr class="my-5"/>
                                    <div class="text-left max-h-96 overflow-auto">
                                        <ul class="divide-y divide-gray-200" v-if="activity_logs && activity_logs.length > 0">
                                            <li v-for="(log, index) in activity_logs" :key="index" class="py-4">
                                                <div class="text-sm font-medium text-gray-900 capitalize">
                                                    <strong>{{ log.description }}</strong> - {{ log.title }}
                                                </div>
                                                <div class="mt-2 text-sm text-gray-700" v-if="Object.keys(log.properties).length > 0">
                                                    <p>
                                                        <strong>Updated Fields:</strong>
                                                    </p>
                                                    <ul class="ml-4 mt-2 list-disc">
                                                        <li v-for="(value, key, idx) in log.properties.attributes"
                                                            v-show="idx < 3 || expandedLogs[index]"
                                                            :key="key">
                                                            <strong v-if="key">{{ key }}:</strong>
                                                            <span v-if="log.properties.old" class="text-gray-500 line-through">{{
                                                                log.properties.old[key] }}</span>
                                                            →
                                                            <span v-if="value" class="text-green-600">{{ value }}</span>
                                                        </li>
                                                    </ul>
                                                    <!-- Show More / Show Less Button -->
                                                    <div v-if="Object.keys(log.properties.attributes).length > 3"
                                                        class="mt-2">
                                                        <button type="button"
                                                            class="text-xs text-indigo-600 hover:underline"
                                                            @click="toggleExpanded(index)">
                                                            <strong>{{ expandedLogs[index] ? 'Show Less' : 'Show More' }}</strong>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div class="mt-2 text-xs text-gray-500">
                                                    <p>
                                                        <strong>Event:</strong> {{ log.event }}
                                                    </p>
                                                    <p>
                                                        <strong>Causer ID:</strong> {{ log.user?.first_name }} {{ log.user?.last_name }}
                                                    </p>
                                                    <p>
                                                        <strong>Timestamp:</strong>
                                                        {{ new Date(log.created_at).toLocaleString() }}
                                                    </p>
                                                </div>
                                            </li>
                                        </ul>
                                        <div class="text-center" v-else>
                                            <p class="mt-1 text-sm text-gray-500">No Logs.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-6 w-full">
                                <button type="button"
                                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                                    @click="$emit('close')" ref="cancelButtonRef">close</button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { usePage } from '@inertiajs/vue3'

// Define props
defineProps({
    open: { type: Boolean, required: true },
    activity_logs: { type: Array, required: false }
})

// Define a close emit
defineEmits(['close'])

// Manage expanded state for logs
import { ref } from 'vue'

const expandedLogs = ref([])

// Toggle expanded state for a specific log
const toggleExpanded = (index) => {
    expandedLogs.value[index] = !expandedLogs.value[index]
}
</script>
